import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Upload, X, Plus, Trash2, ChevronLeft, ChevronRight, ImageIcon, Palette, Wand2, FileDown, Link as LinkIcon, Youtube, Box, Printer, Car, Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby } from "lucide-react";
import { useTranslation } from "react-i18next";
import ProductLanguageHelper from "@/i18n/ProductLanguageHelper";
import TutorialBanner, { SECTION_TITLES, YoutubePopup, useSectionTutorials } from "@/components/TutorialBanner";
import ProductDescriptionEditor from "@/components/ProductDescriptionEditor";
import ProductModelViewer from "@/components/ProductModelViewer";
import { extractYoutubeId, parseGalleryItem, youtubeThumb } from "@/lib/productGallery";
import PrintAreaEditor from "@/components/PrintAreaEditor";
import { emptyPrintConfig } from "@/lib/customPrint";
import { emptyCarParts, newFitmentRow, uniqueMakes, uniqueModels, uniqueYears, uniqueTrims } from "@/lib/carParts";
import CosmosDesignFields, { EMPTY_COSMOS_DESIGN } from "./CosmosDesignFields";

const BADGE_ICONS = { Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby };
const BADGE_COLORS = ["#FDE7F3", "#EDE9FE", "#FEF3C7", "#D1FAE5", "#E0F2FE", "#FFEDD5"];

const EMPTY_SHIPPING = {
  weight: "", weight_unit: "kg", length: "", width: "", height: "", dimension_unit: "cm",
};

function shippingFrom(raw) {
  const s = raw || {};
  const num = (v) => (v === null || v === undefined || v === "" ? "" : v);
  return {
    weight: num(s.weight),
    weight_unit: ["kg", "g", "lb", "oz"].includes(s.weight_unit) ? s.weight_unit : "kg",
    length: num(s.length),
    width: num(s.width),
    height: num(s.height),
    dimension_unit: ["cm", "in"].includes(s.dimension_unit) ? s.dimension_unit : "cm",
  };
}

const EMPTY_SEO = {
  meta_title: "", meta_description: "", meta_keywords: "", focus_keyword: "",
  canonical_url: "", og_title: "", og_image: "", robots_index: true,
};

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `v_${Math.random().toString(36).slice(2, 10)}`;
}
function newVariant(attributes = {}, name = "") {
  return { id: uid(), name, attributes, price: "", mrp: "", stock: "", sku: "", image_url: null };
}
function newOption() {
  return { id: uid(), name: "", type: "text", values: [] };
}
function newOptionValue(hex = "#111827") {
  return { id: uid(), value: "", hex };
}
function newCfField(type) {
  const labels = { paid_name: "Name", paid_dropdown: "Option", paid_image: "Upload image", quantity: "Quantity" };
  const field = { id: `cf_${uid().replace(/-/g, "").slice(0, 10)}`, type, label: labels[type] || "Field", required: true, price: 0, help: "", options: [] };
  if (type === "paid_dropdown") field.options = [{ id: `op_${uid().slice(0, 8)}`, label: "Option 1", price: 0 }];
  if (type === "quantity") field.options = [{ id: `op_${uid().slice(0, 8)}`, label: "50 pcs", qty: 50, price: 0 }];
  return field;
}
// Reverse-engineer the option definitions from saved variants (edit mode).
function deriveOptions(variants) {
  const keys = [];
  (variants || []).forEach((v) => Object.keys(v.attributes || {}).forEach((k) => {
    if (k.endsWith("_hex")) return;
    if (!keys.includes(k)) keys.push(k);
  }));
  return keys.map((k) => {
    const isColor = k.toLowerCase() === "color";
    const seen = new Map();
    (variants || []).forEach((v) => {
      const val = v.attributes?.[k];
      if (val && !seen.has(val)) seen.set(val, { id: uid(), value: val, hex: v.attributes?.[`${k}_hex`] || "#111827" });
    });
    return { id: uid(), name: k.charAt(0).toUpperCase() + k.slice(1), type: isColor ? "color" : "text", values: Array.from(seen.values()) };
  });
}

function VariantImage({ url, onChange, testid }) {
  const uploadFile = useFileUpload();
  const [uploading, setUploading] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "product");
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
        {url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">No image</span>}
      </div>
      <label className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 cursor-pointer" data-testid={testid}>
        <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : (url ? "Replace" : "Upload")}
        <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} />
      </label>
      {url && <button type="button" className="text-xs text-red-600" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>Remove</button>}
    </div>
  );
}

function slugifyProduct(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const uploadFile = useFileUpload();

  const [form, setForm] = useState({
    name: "", slug: "", description: "", short_description: "", images: [], price: 0, mrp: 0, stock: "", sku: "", category_id: "", category_ids: [], tag_ids: [], is_active: true, is_featured: false, variants: [],
    fake_rating_enabled: false, fake_rating_avg: "", fake_rating_count: 0,
    is_digital: false, digital_file_url: "", digital_link: "", digital_filename: "", model_3d_url: "",
    custom_form: { enabled: false, cta: "Get Started", fields: [] },
    country_prices: {},
    custom_print: emptyPrintConfig(),
    car_parts: emptyCarParts(),
    page_badges: [],
    cosmos_design: { ...EMPTY_COSMOS_DESIGN },
    seo: { ...EMPTY_SEO },
    shipping: { ...EMPTY_SHIPPING },
    shipping_price: "",
    author_id: "",
  });
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Inline "create category" dialog
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", image_url: "" });
  const [catUploading, setCatUploading] = useState(false);
  const [catSaving, setCatSaving] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: "", image_url: "" });
  const [tagUploading, setTagUploading] = useState(false);
  const [tagSaving, setTagSaving] = useState(false);
  const [quota, setQuota] = useState(null);
  const [digitalAddon, setDigitalAddon] = useState(null);
  const [formAddon, setFormAddon] = useState(null);
  const [printAddon, setPrintAddon] = useState(null);
  const [carPartsAddon, setCarPartsAddon] = useState(null);
  const [enhancerAddon, setEnhancerAddon] = useState(null);
  const [cosmosAddon, setCosmosAddon] = useState(null);
  const [storeTheme, setStoreTheme] = useState("");
  const [fitmentGarage, setFitmentGarage] = useState({ makes: [], tree: {} });
  const [currencyAddon, setCurrencyAddon] = useState(null);
  const [shipPriceOn, setShipPriceOn] = useState(false);
  const [authorOn, setAuthorOn] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [digitalUploading, setDigitalUploading] = useState(false);
  const [tutorial, setTutorial] = useState(null);
  const sectionTutorials = useSectionTutorials();
  const [printHelpOpen, setPrintHelpOpen] = useState(false);
  const [carPartsHelpOpen, setCarPartsHelpOpen] = useState(false);
  const [formTab, setFormTab] = useState("details");
  const [ytUrl, setYtUrl] = useState("");
  const [slugTouched, setSlugTouched] = useState(isEdit);

  useEffect(() => {
    api.get("/i18n/languages").then(({ data }) => {
      const code = (i18n.language || "en").split("-")[0];
      setTutorial((data.tutorials || {})[code] || null);
    }).catch(() => {});
  }, [i18n.language]);

  const uploadCatImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "category");
    setCatUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setNewCat((c) => ({ ...c, image_url: data.url }));
    } catch (e) { toast.error(extractErr(e)); } finally { setCatUploading(false); }
  };

  const createCategory = async () => {
    if (!newCat.name.trim()) { toast.error("Category name is required"); return; }
    setCatSaving(true);
    try {
      const { data } = await api.post("/merchant/categories", { name: newCat.name.trim(), image_url: newCat.image_url || null, sort_order: 0 });
      const { data: fresh } = await api.get("/merchant/categories");
      setCats(fresh);
      setForm((f) => {
        const ids = [...new Set([...(f.category_ids || []), data.id, f.category_id].filter(Boolean))];
        return { ...f, category_id: ids[0] || data.id, category_ids: ids };
      });
      toast.success("Category created");
      setCatOpen(false);
      setNewCat({ name: "", image_url: "" });
    } catch (e) { toast.error(extractErr(e)); } finally { setCatSaving(false); }
  };

  const uploadTagImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "category");
    setTagUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setNewTag((c) => ({ ...c, image_url: data.url }));
    } catch (e) { toast.error(extractErr(e)); } finally { setTagUploading(false); }
  };

  const createTag = async () => {
    if (!newTag.name.trim()) { toast.error("Tag name is required"); return; }
    setTagSaving(true);
    try {
      const { data } = await api.post("/merchant/product-tags", { name: newTag.name.trim(), image_url: newTag.image_url || null, sort_order: 0 });
      const { data: fresh } = await api.get("/merchant/product-tags");
      setTags(fresh);
      setForm((f) => ({ ...f, tag_ids: [...new Set([...(f.tag_ids || []), data.id])] }));
      setTagOpen(false);
      setNewTag({ name: "", image_url: "" });
      toast.success("Tag created");
    } catch (e) { toast.error(extractErr(e)); } finally { setTagSaving(false); }
  };

  useEffect(() => {
    api.get("/merchant/config").then(({ data }) => setStoreTheme(data.storefront_theme || "")).catch(() => {});
    api.get("/merchant/categories").then(({ data }) => setCats(data));
    api.get("/merchant/product-tags").then(({ data }) => setTags(data));
    api.get("/merchant/addons").then(({ data }) => {
      const items = data.items || [];
      setDigitalAddon(items.find((x) => x.id === "digital_product") || null);
      setFormAddon(items.find((x) => x.id === "custom_form") || null);
      setPrintAddon(items.find((x) => x.id === "custom_print") || null);
      setCarPartsAddon(items.find((x) => x.id === "car_parts") || null);
      setEnhancerAddon(items.find((x) => x.id === "product_page_enhancer") || null);
      setCosmosAddon(items.find((x) => x.id === "cosmos_design_enhancer") || null);
      setCurrencyAddon(items.find((x) => x.id === "multi_currency") || null);
      setShipPriceOn(Boolean(items.find((x) => x.id === "per_product_shipping")?.entitlement?.enabled));
      const authorsLive = Boolean(items.find((x) => x.id === "author_pages")?.entitlement?.enabled);
      setAuthorOn(authorsLive);
      if (authorsLive) api.get("/merchant/authors").then(({ data }) => setAuthors(data.items || [])).catch(() => setAuthors([]));
      const mc = items.find((x) => x.id === "multi_currency");
      if (mc?.entitlement?.enabled) {
        api.get("/merchant/config/multi-currency").then(({ data: d }) => {
          setEnabledCountries((d.multi_currency?.countries || []).filter((c) => c.enabled));
        }).catch(() => {});
      }
    }).catch(() => {});
    if (!isEdit) {
      api.get("/merchant/product-quota").then(({ data }) => setQuota(data)).catch(() => {});
    }
    if (isEdit) {
      api.get(`/merchant/products/${id}`).then(({ data }) => {
        setForm((f) => ({
          ...f, ...data,
          description: data.description || "", short_description: data.short_description || "", sku: data.sku || "",
          images: Array.isArray(data.images) ? data.images.filter(Boolean) : [],
          category_id: data.category_id || (data.category_ids || [])[0] || "",
          category_ids: (Array.isArray(data.category_ids) && data.category_ids.length ? data.category_ids : (data.category_id ? [data.category_id] : [])),
          tag_ids: Array.isArray(data.tag_ids) ? data.tag_ids : [],
          stock: data.stock === null || data.stock === undefined ? "" : data.stock,
          seo: { ...EMPTY_SEO, ...(data.seo || {}) },
          is_digital: Boolean(data.is_digital),
          digital_file_url: data.digital_file_url || "",
          digital_link: data.digital_link || "",
          digital_filename: data.digital_filename || "",
          model_3d_url: data.model_3d_url || "",
          custom_form: data.custom_form || { enabled: false, cta: "Get Started", fields: [] },
          custom_print: { ...emptyPrintConfig(), ...(data.custom_print || {}) },
          car_parts: { ...emptyCarParts(), ...(data.car_parts || {}) },
          country_prices: data.country_prices || {},
          page_badges: Array.isArray(data.page_badges) ? data.page_badges : [],
          cosmos_design: { ...EMPTY_COSMOS_DESIGN, ...(data.cosmos_design || {}) },
          shipping: shippingFrom(data.shipping),
          variants: (data.variants || []).map((v) => ({ ...v, stock: v.stock === null || v.stock === undefined ? "" : v.stock })),
        }));
        setOptions(deriveOptions(data.variants || []));
        setSlugTouched(true);
      }).catch(() => toast.error("Not found")).finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (!carPartsAddon?.entitlement?.enabled) return undefined;
    api.get("/merchant/car-parts/garage").then(({ data }) => setFitmentGarage(data || { makes: [], tree: {} })).catch(() => setFitmentGarage({ makes: [], tree: {} }));
    return undefined;
  }, [carPartsAddon?.entitlement?.enabled]);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "product");
        const data = await uploadFile("/merchant/media/upload", fd);
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success(`${list.length} image${list.length > 1 ? "s" : ""} uploaded`);
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  const addYoutube = () => {
    const id = extractYoutubeId(ytUrl);
    if (!id) { toast.error("Enter a valid YouTube URL"); return; }
    if (form.images.length >= 8) { toast.error("You can add up to 8 gallery slides"); return; }
    setForm((f) => ({
      ...f,
      images: [...f.images, { type: "youtube", url: `https://www.youtube.com/watch?v=${id}`, video_id: id }],
    }));
    setYtUrl("");
    toast.success("YouTube video added — use arrows to set slide position");
  };

  const removeImg = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  const moveImg = (idx, dir) => setForm((f) => {
    const arr = [...f.images];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...f, images: arr };
  });

  // -------- Variant options (Shopify-style) --------
  const updateOption = (oid, patch) => setOptions((os) => os.map((o) => (o.id === oid ? { ...o, ...patch } : o)));
  const addOption = () => setOptions((os) => (os.length >= 3 ? os : [...os, newOption()]));
  const removeOption = (oid) => setOptions((os) => os.filter((o) => o.id !== oid));
  const addOptionValue = (o) => updateOption(o.id, { values: [...o.values, newOptionValue(o.type === "color" ? "#3b82f6" : "#111827")] });
  const updateOptionValue = (o, vid, patch) => updateOption(o.id, { values: o.values.map((v) => (v.id === vid ? { ...v, ...patch } : v)) });
  const removeOptionValue = (o, vid) => updateOption(o.id, { values: o.values.filter((v) => v.id !== vid) });

  const sigOf = (attrs) => Object.keys(attrs || {}).filter((k) => !k.endsWith("_hex")).sort().map((k) => `${k}=${attrs[k]}`).join("|");

  const generateVariants = () => {
    const opts = options
      .map((o) => ({ ...o, name: o.name.trim(), values: o.values.filter((x) => x.value.trim()) }))
      .filter((o) => o.name && o.values.length);
    if (!opts.length) { toast.error("Add at least one option with values first"); return; }
    let combos = [[]];
    opts.forEach((o) => {
      const next = [];
      combos.forEach((c) => o.values.forEach((val) => next.push([...c, { opt: o, val }])));
      combos = next;
    });
    const prevBySig = {};
    (form.variants || []).forEach((v) => { prevBySig[sigOf(v.attributes)] = v; });
    const built = combos.map((combo) => {
      const attributes = {};
      combo.forEach(({ opt, val }) => {
        const key = opt.name.toLowerCase();
        attributes[key] = val.value.trim();
        if (opt.type === "color") attributes[`${key}_hex`] = val.hex;
      });
      const name = combo.map(({ val }) => val.value.trim()).join(" / ");
      const prev = prevBySig[sigOf(attributes)];
      return prev ? { ...prev, name, attributes } : newVariant(attributes, name);
    });
    setForm((f) => ({ ...f, variants: built }));
    toast.success(`${built.length} variant${built.length === 1 ? "" : "s"} generated`);
  };

  const addManualVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), newVariant({}, "")] }));
  const updateVariant = (idx, patch) => setForm((f) => { const next = [...f.variants]; next[idx] = { ...next[idx], ...patch }; return { ...f, variants: next }; });
  const removeVariant = (idx) => setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && quota && quota.remaining <= 0) {
      toast.error(`posting limit ${quota.product_limit}products reached, contact support to increase rate limit`);
      return;
    }
    const productSlug = slugifyProduct(form.slug);
    if (!productSlug) {
      toast.error("Product URL is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: productSlug,
        price: Number(form.price),
        mrp: form.mrp ? Number(form.mrp) : null,
        stock: form.stock === "" || form.stock === null || form.stock === undefined ? null : (Number.isNaN(parseInt(form.stock, 10)) ? null : parseInt(form.stock, 10)),
        category_id: (form.category_ids || [])[0] || form.category_id || null,
        category_ids: form.category_ids || [],
        tag_ids: form.tag_ids || [],
        fake_rating_enabled: Boolean(form.fake_rating_enabled),
        fake_rating_avg: form.fake_rating_enabled && form.fake_rating_avg !== "" ? Number(form.fake_rating_avg) : null,
        fake_rating_count: form.fake_rating_enabled ? parseInt(form.fake_rating_count || 0, 10) : 0,
        is_digital: Boolean(form.is_digital),
        product_type: form.is_digital ? "digital" : "physical",
        digital_file_url: form.is_digital ? (form.digital_file_url || null) : null,
        digital_link: form.is_digital ? (form.digital_link || null) : null,
        digital_filename: form.is_digital ? (form.digital_filename || null) : null,
        model_3d_url: form.model_3d_url || null,
        seo: { ...EMPTY_SEO, ...(form.seo || {}) },
        shipping: (() => {
          const s = shippingFrom(form.shipping);
          const n = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
          return {
            weight: n(s.weight),
            weight_unit: s.weight_unit,
            length: n(s.length),
            width: n(s.width),
            height: n(s.height),
            dimension_unit: s.dimension_unit,
          };
        })(),
        shipping_price: form.shipping_price === "" || form.shipping_price == null ? 0 : Number(form.shipping_price),
        author_id: form.author_id || "",
        variants: (form.variants || []).map((v) => ({
          id: v.id,
          name: v.name,
          attributes: v.attributes || {},
          price: v.price === "" || v.price === null ? null : Number(v.price),
          mrp: v.mrp === "" || v.mrp === null ? null : Number(v.mrp),
          stock: v.stock === "" || v.stock === null || v.stock === undefined ? null : (Number.isNaN(parseInt(v.stock, 10)) ? null : parseInt(v.stock, 10)),
          sku: v.sku || "",
          image_url: v.image_url || null,
        })),
      };
      if (isEdit) await api.put(`/merchant/products/${id}`, payload);
      else await api.post("/merchant/products", payload);
      toast.success(isEdit ? t("products.updated") : t("products.created"));
      nav("/admin/products");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="max-w-3xl">
      <ProductLanguageHelper language={(i18n.language || "en").split("-")[0]} tutorial={tutorial} />
      <TutorialBanner section="products" url={sectionTutorials.products} title={SECTION_TITLES.products} testId="tutorial-banner-products-form" className="mb-5" />
      <Link to="/admin/products" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="product-form-back"><ArrowLeft className="w-4 h-4" /> {t("common.back")}</Link>
      <h1 className="font-display text-3xl font-bold text-slate-900">{isEdit ? t("products.editProduct") : t("products.newProduct")}</h1>
      {!isEdit && quota && quota.remaining <= 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="pf-limit-warning">
          posting limit {quota.product_limit}products reached, contact support to increase rate limit
        </div>
      )}

      <form onSubmit={submit} className="mt-6 space-y-5">
        <div className="flex items-center gap-2 border-b border-slate-200">
          <button type="button" data-testid="tab-product-details" onClick={() => setFormTab("details")}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${formTab === "details" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Details</button>
          <button type="button" data-testid="tab-product-seo" onClick={() => setFormTab("seo")}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 ${formTab === "seo" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Product SEO</button>
        </div>

        {formTab === "seo" && (
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="product-seo-card">
            <p className="text-sm text-slate-600">Search and social previews for this product. Empty fields fall back to the product name, description, and cover image.</p>
            <div>
              <Label>Meta title <span className="text-slate-400 text-xs">{(form.seo?.meta_title || "").length}/180</span></Label>
              <Input data-testid="pf-seo-title" className="mt-1" maxLength={180} value={form.seo?.meta_title || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, meta_title: e.target.value } })} placeholder={form.name ? `${form.name} — your store` : "Product title for Google"} />
            </div>
            <div>
              <Label>Meta description <span className="text-slate-400 text-xs">{(form.seo?.meta_description || "").length}/320</span></Label>
              <Textarea rows={3} data-testid="pf-seo-description" className="mt-1" maxLength={320} value={form.seo?.meta_description || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, meta_description: e.target.value } })} placeholder="One or two sentences shoppers see in search results." />
            </div>
            <div>
              <Label>Focus keyword</Label>
              <Input data-testid="pf-seo-focus" className="mt-1" value={form.seo?.focus_keyword || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, focus_keyword: e.target.value } })} placeholder="e.g. organic cotton t-shirt" />
            </div>
            <div>
              <Label>Meta keywords</Label>
              <Input data-testid="pf-seo-keywords" className="mt-1" value={form.seo?.meta_keywords || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, meta_keywords: e.target.value } })} placeholder="comma, separated, keywords" />
            </div>
            <div>
              <Label>Canonical URL <span className="text-slate-400 text-xs">{t("common.optional")}</span></Label>
              <Input data-testid="pf-seo-canonical" className="mt-1 font-mono-nums" value={form.seo?.canonical_url || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, canonical_url: e.target.value } })} placeholder="https://…" />
            </div>
            <div>
              <Label>Open Graph title <span className="text-slate-400 text-xs">{t("common.optional")}</span></Label>
              <Input data-testid="pf-seo-og-title" className="mt-1" value={form.seo?.og_title || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, og_title: e.target.value } })} placeholder="Defaults to product name" />
            </div>
            <div>
              <Label>Open Graph image URL <span className="text-slate-400 text-xs">{t("common.optional")}</span></Label>
              <Input data-testid="pf-seo-og-image" className="mt-1 font-mono-nums" value={form.seo?.og_image || ""} onChange={(e) => setForm({ ...form, seo: { ...form.seo, og_image: e.target.value } })} placeholder="Leave blank to use the cover image" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
              <div>
                <Label>Allow search engines to index this product</Label>
                <p className="text-xs text-slate-500 mt-1">Turn off for unlisted or private products.</p>
              </div>
              <Switch checked={form.seo?.robots_index !== false} onCheckedChange={(v) => setForm({ ...form, seo: { ...form.seo, robots_index: v } })} data-testid="pf-seo-robots" />
            </div>
          </Card>
        )}

        {formTab === "details" && (<>
        <Card className="p-6 space-y-4 border-slate-200/70">
          <div>
            <Label>{t("products.name")}</Label>
            <Input required data-testid="pf-name" value={form.name} onChange={(e) => {
              const name = e.target.value;
              setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugifyProduct(name) }));
            }} className="mt-1" />
          </div>
          <div>
            <Label>Product URL <span className="text-red-500">*</span></Label>
            <div className="mt-1 flex items-center gap-0 rounded-md border border-slate-200 bg-white overflow-hidden">
              <span className="px-3 text-sm text-slate-400 shrink-0 border-r border-slate-200 bg-slate-50 h-10 inline-flex items-center">/product/</span>
              <Input
                required
                data-testid="pf-slug"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((f) => ({ ...f, slug: slugifyProduct(e.target.value) }));
                }}
                placeholder="your-product-name"
                className="border-0 rounded-none shadow-none focus-visible:ring-0 font-mono-nums"
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">This is the link shoppers open. You can change it when adding, editing, or duplicating a product.</p>
          </div>
          <div>
            <Label>{t("products.shortDescription")} <span className="text-slate-400 text-xs">{t("common.optional")}</span></Label>
            <Textarea rows={2} maxLength={300} data-testid="pf-short-description" placeholder="A punchy one or two line summary shoppers see first…" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label>{t("products.description")}</Label>
            <ProductDescriptionEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} />
          </div>
          {storeTheme === "cosmos" && cosmosAddon?.entitlement?.enabled && (
            <CosmosDesignFields value={form.cosmos_design} onChange={(cosmos_design) => setForm({ ...form, cosmos_design })} />
          )}
          {enhancerAddon?.entitlement?.enabled && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="ppe-badges">
              <div>
                <div className="font-semibold text-slate-900">Product page badges</div>
                <p className="text-xs text-slate-500 mt-1">These colored labels show under the product name. Pick an icon and a background color for each one.</p>
              </div>
              {(form.page_badges || []).map((badge, idx) => {
                const Icon = BADGE_ICONS[badge.icon] || Sparkles;
                return (
                  <div key={idx} className="rounded-lg border border-slate-100 p-3 space-y-2" data-testid={`ppe-badge-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-stone-800" style={{ background: badge.color || BADGE_COLORS[0] }}>
                        <Icon className="w-3.5 h-3.5" /> {badge.text || "Badge"}
                      </span>
                      <button type="button" className="ml-auto text-slate-400 hover:text-red-600" aria-label="Remove badge" onClick={() => setForm({ ...form, page_badges: form.page_badges.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Input data-testid={`ppe-badge-text-${idx}`} maxLength={40} value={badge.text} placeholder="Beginner friendly" onChange={(e) => {
                      const next = [...form.page_badges];
                      next[idx] = { ...badge, text: e.target.value };
                      setForm({ ...form, page_badges: next });
                    }} />
                    <div className="flex flex-wrap gap-1" data-testid={`ppe-badge-icons-${idx}`}>
                      {Object.entries(BADGE_ICONS).map(([name, Ic]) => (
                        <button key={name} type="button" aria-label={name} title={name} onClick={() => {
                          const next = [...form.page_badges];
                          next[idx] = { ...badge, icon: name };
                          setForm({ ...form, page_badges: next });
                        }} className={`w-8 h-8 rounded-lg border flex items-center justify-center ${badge.icon === name ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
                          <Ic className="w-3.5 h-3.5" />
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2" data-testid={`ppe-badge-colors-${idx}`}>
                      {BADGE_COLORS.map((c) => (
                        <button key={c} type="button" aria-label={c} onClick={() => {
                          const next = [...form.page_badges];
                          next[idx] = { ...badge, color: c };
                          setForm({ ...form, page_badges: next });
                        }} className={`w-7 h-7 rounded-full border-2 ${String(badge.color).toUpperCase() === c ? "border-slate-900" : "border-white"}`} style={{ background: c }} />
                      ))}
                      <label className="text-xs text-slate-500 inline-flex items-center gap-2">
                        Custom
                        <input type="color" data-testid={`ppe-badge-color-${idx}`} value={/^#[0-9A-Fa-f]{6}$/.test(badge.color || "") ? badge.color : BADGE_COLORS[0]} onChange={(e) => {
                          const next = [...form.page_badges];
                          next[idx] = { ...badge, color: e.target.value };
                          setForm({ ...form, page_badges: next });
                        }} />
                      </label>
                    </div>
                  </div>
                );
              })}
              <Button type="button" variant="outline" data-testid="ppe-badge-add" disabled={(form.page_badges || []).length >= 8} onClick={() => setForm({
                ...form,
                page_badges: [...(form.page_badges || []), { text: "", icon: "Sparkles", color: BADGE_COLORS[(form.page_badges || []).length % BADGE_COLORS.length] }],
              })}><Plus className="w-4 h-4 mr-1" /> Add badge</Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
            <div>
              <Label>{t("products.price")}</Label>
              <Input required type="number" step="0.01" data-testid="pf-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 font-mono-nums" />
            </div>
            <div>
              <Label>{t("products.mrp")} <span className="text-slate-400 text-xs">{t("common.optional")}</span></Label>
              <Input type="number" step="0.01" data-testid="pf-mrp" value={form.mrp ?? ""} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="mt-1 font-mono-nums" />
            </div>
            <div>
              <Label>{t("products.stock")} <span className="text-slate-400 text-xs font-normal">blank = unlimited</span></Label>
              <Input type="number" min="0" data-testid="pf-stock" placeholder="Unlimited" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 font-mono-nums" />
              {form.custom_form?.enabled && <p className="text-[11px] text-amber-700 mt-1">Custom Form is on — storefront stock quantity is hidden (made to order).</p>}
            </div>
            <div>
              <Label>{t("products.sku")}</Label>
              <Input data-testid="pf-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1 font-mono-nums" />
            </div>
          </div>
          {authorOn && (
            <div>
              <Label>Author</Label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="pf-author"
                value={form.author_id || ""}
                onChange={(e) => setForm({ ...form, author_id: e.target.value })}
              >
                <option value="">No author</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">Shown under the product title. The name links to this author’s page. Manage authors in the Authors tab.</p>
            </div>
          )}
          {!form.is_digital && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="pf-shipping">
              <div>
                <div className="font-semibold text-slate-900">Shipping</div>
                <p className="text-xs text-slate-500 mt-1">
                  {shipPriceOn
                    ? "Shoppers pay this shipping price for each unit, added to the product price at checkout. Flat rate, zones, and Shiprocket stay paused while Per Product Shipping Price is on."
                    : "Weight and package size (length × width × breadth), the same details Shopify stores for a physical product. Shiprocket uses them when you create a shipment: combined weight, the longest side, the widest side, and stacked breadth. Shoppers are charged from Settings → Shipping, not from these numbers."}
                </p>
              </div>
              {shipPriceOn && (
                <div>
                  <Label>Shipping price</Label>
                  <Input type="number" min="0" step="0.01" data-testid="pf-shipping-price" className="mt-1 font-mono-nums max-w-xs" placeholder="0" value={form.shipping_price ?? ""} onChange={(e) => setForm({ ...form, shipping_price: e.target.value })} />
                  <p className="text-xs text-slate-500 mt-1">Per unit. Checkout shows the product price, then this shipping price in small text, and charges the total.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Weight</Label>
                  <div className="mt-1 flex gap-2">
                    <Input type="number" min="0" step="0.001" data-testid="pf-ship-weight" className="font-mono-nums" placeholder="0.5" value={form.shipping?.weight ?? ""} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), weight: e.target.value } })} />
                    <select className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="pf-ship-weight-unit" value={form.shipping?.weight_unit || "kg"} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), weight_unit: e.target.value } })}>
                      <option value="kg">kg</option>
                      <option value="g">g</option>
                      <option value="lb">lb</option>
                      <option value="oz">oz</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Size unit</Label>
                  <select className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="pf-ship-dim-unit" value={form.shipping?.dimension_unit || "cm"} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), dimension_unit: e.target.value } })}>
                    <option value="cm">cm</option>
                    <option value="in">in</option>
                  </select>
                </div>
                <div>
                  <Label>Length</Label>
                  <Input type="number" min="0" step="0.1" data-testid="pf-ship-length" className="mt-1 font-mono-nums" value={form.shipping?.length ?? ""} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), length: e.target.value } })} />
                </div>
                <div>
                  <Label>Width</Label>
                  <Input type="number" min="0" step="0.1" data-testid="pf-ship-width" className="mt-1 font-mono-nums" value={form.shipping?.width ?? ""} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), width: e.target.value } })} />
                </div>
                <div>
                  <Label>Breadth</Label>
                  <Input type="number" min="0" step="0.1" data-testid="pf-ship-breadth" className="mt-1 font-mono-nums" value={form.shipping?.height ?? ""} onChange={(e) => setForm({ ...form, shipping: { ...shippingFrom(form.shipping), height: e.target.value } })} />
                </div>
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between">
              <Label>{t("products.category")}</Label>
              <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 h-7 px-2" onClick={() => setCatOpen(true)} data-testid="pf-new-category"><Plus className="w-3.5 h-3.5 mr-1" /> {t("products.newCategory")}</Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Select one or more categories. Shoppers will find this product in each selected category.</p>
            <div className="mt-2 rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="pf-category">
              {cats.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-400">{t("products.noCategory")}</div>
              ) : cats.filter((c) => !c.parent_id).flatMap((c) => [
                { ...c, _label: c.name },
                ...cats.filter((ch) => ch.parent_id === c.id).map((ch) => ({ ...ch, _label: `— ${ch.name}` })),
              ]).map((c) => {
                const checked = (form.category_ids || []).includes(c.id);
                return (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox
                      checked={checked}
                      data-testid={`pf-cat-${c.slug}`}
                      onCheckedChange={() => {
                        setForm((f) => {
                          const cur = f.category_ids || [];
                          const next = checked ? cur.filter((id) => id !== c.id) : [...cur, c.id];
                          return { ...f, category_ids: next, category_id: next[0] || "" };
                        });
                      }}
                    />
                    <span>{c._label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Tags</Label>
              <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 h-7 px-2" onClick={() => setTagOpen(true)} data-testid="pf-new-tag"><Plus className="w-3.5 h-3.5 mr-1" /> New tag</Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Unlimited tags. Reuse tags you already created.</p>
            <div className="mt-2 rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="pf-tags">
              {tags.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-400">No tags yet</div>
              ) : tags.map((tg) => {
                const checked = (form.tag_ids || []).includes(tg.id);
                return (
                  <label key={tg.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox
                      checked={checked}
                      data-testid={`pf-tag-${tg.slug}`}
                      onCheckedChange={() => {
                        setForm((f) => {
                          const cur = f.tag_ids || [];
                          const next = checked ? cur.filter((id) => id !== tg.id) : [...cur, tg.id];
                          return { ...f, tag_ids: next };
                        });
                      }}
                    />
                    {tg.image_url ? <img src={mediaUrl(tg.image_url)} alt="" className="w-6 h-6 rounded object-cover" /> : null}
                    <span>{tg.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-digital-card">
          <div>
            <Label>Product type</Label>
            <p className="text-xs text-slate-500 mt-1">Physical ships to the customer. Digital delivers a file or link in their account after purchase.</p>
          </div>
          {!(digitalAddon?.entitlement?.enabled || form.is_digital) ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              Digital products require the <Link to="/admin/addons" className="underline font-medium">Digital Product</Link> app from App Store.
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <button type="button" data-testid="pf-type-physical" onClick={() => setForm({ ...form, is_digital: false })}
                  className={`px-3 py-2 rounded-lg text-sm border ${!form.is_digital ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Physical</button>
                <button type="button" data-testid="pf-type-digital" onClick={() => setForm({ ...form, is_digital: true })}
                  className={`px-3 py-2 rounded-lg text-sm border ${form.is_digital ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200"}`}>Digital</button>
              </div>
              {form.is_digital && (
                <div className="space-y-3">
                  <div>
                    <Label>File (any format, max 20MB)</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-blue-600 cursor-pointer">
                        <Upload className="w-4 h-4" /> {digitalUploading ? "Uploading…" : (form.digital_file_url ? "Replace file" : "Upload file")}
                        <input type="file" hidden data-testid="pf-digital-file" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 20 * 1024 * 1024) { toast.error("File exceeds 20MB"); return; }
                          const fd = new FormData(); fd.append("file", file);
                          setDigitalUploading(true);
                          try {
                            const data = await uploadFile("/merchant/media/upload-digital", fd);
                            setForm((f) => ({ ...f, digital_file_url: data.url, digital_filename: data.filename || file.name }));
                            toast.success("File uploaded");
                          } catch (err) { toast.error(extractErr(err)); } finally { setDigitalUploading(false); }
                        }} />
                      </label>
                      {form.digital_file_url && (
                        <span className="text-xs text-slate-600 truncate flex items-center gap-1"><FileDown className="w-3.5 h-3.5" />{form.digital_filename || "Attached file"}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Or link to the file</Label>
                    <div className="relative mt-1">
                      <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input data-testid="pf-digital-link" placeholder="https://…" value={form.digital_link} onChange={(e) => setForm({ ...form, digital_link: e.target.value })} className="pl-9" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {printAddon?.entitlement?.enabled && (
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-custom-print">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="inline-flex items-center gap-2"><Printer className="w-4 h-4" /> Custom Print</Label>
                <p className="text-xs text-slate-500 mt-1">Upload a blank product photo, then draw a box or use <span className="font-semibold">Trace shape</span> to drop pointers around an irregular window (photo frame, calendar cutout, etc.).</p>
              </div>
              <Switch
                checked={Boolean(form.custom_print?.enabled)}
                onCheckedChange={(v) => setForm({ ...form, custom_print: { ...(form.custom_print || emptyPrintConfig()), enabled: v } })}
                data-testid="pf-print-enabled"
              />
            </div>
            {form.custom_print?.enabled ? (
              <div className="space-y-4">
                {extractYoutubeId(sectionTutorials.custom_print) ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setPrintHelpOpen(true)}
                      data-testid="pf-print-howto"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                      <Youtube className="w-4 h-4" /> How to use custom print addon?
                    </button>
                    <YoutubePopup
                      open={printHelpOpen}
                      onOpenChange={setPrintHelpOpen}
                      url={sectionTutorials.custom_print}
                      title="How to use custom print addon?"
                    />
                  </>
                ) : null}
                <div>
                  <Label>Blank product image</Label>
                  <div className="mt-2 flex items-center gap-3">
                    {form.custom_print.base_image_url ? (
                      <img src={mediaUrl(form.custom_print.base_image_url)} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                    ) : null}
                    <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 cursor-pointer" data-testid="pf-print-base-upload">
                      <Upload className="w-4 h-4" /> {form.custom_print.base_image_url ? "Replace" : "Upload"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append("file", file);
                          fd.append("kind", "product");
                          try {
                            const data = await uploadFile("/merchant/media/upload", fd);
                            setForm((f) => ({ ...f, custom_print: { ...(f.custom_print || emptyPrintConfig()), enabled: true, base_image_url: data.url } }));
                          } catch (err) { toast.error(extractErr(err)); }
                        }}
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Customers can</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      { id: "image", label: "Upload image" },
                      { id: "text", label: "Add text" },
                      { id: "both", label: "Both" },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`pf-print-allow-${opt.id}`}
                        onClick={() => setForm({ ...form, custom_print: { ...form.custom_print, allow: opt.id } })}
                        className={`h-8 px-3 rounded-full text-xs font-semibold border ${(form.custom_print.allow || "both") === opt.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <PrintAreaEditor
                  imageUrl={form.custom_print.base_image_url}
                  value={form.custom_print.print_area}
                  onChange={(print_area) => setForm((f) => ({ ...f, custom_print: { ...f.custom_print, print_area } }))}
                />
              </div>
            ) : null}
          </Card>
        )}

        {carPartsAddon?.entitlement?.enabled && (
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-car-parts">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="inline-flex items-center gap-2"><Car className="w-4 h-4" /> Car Parts — vehicle fitment</Label>
                <p className="text-xs text-slate-500 mt-1">Add the vehicles this part fits (Make, Model, Year, Trim). Shoppers select their car to find matching parts — the same Year / Make / Model / Trim pattern used by auto-parts stores.</p>
              </div>
              <Switch
                checked={Boolean(form.car_parts?.enabled)}
                onCheckedChange={(v) => setForm({ ...form, car_parts: { ...(form.car_parts || emptyCarParts()), enabled: v, applications: form.car_parts?.applications?.length ? form.car_parts.applications : [newFitmentRow()] } })}
                data-testid="pf-car-parts-enabled"
              />
            </div>
            {form.car_parts?.enabled ? (
              <div className="space-y-4">
                {extractYoutubeId(sectionTutorials.car_parts) ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setCarPartsHelpOpen(true)}
                      data-testid="pf-car-parts-howto"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                      <Youtube className="w-4 h-4" /> How to use car parts addon?
                    </button>
                    <YoutubePopup
                      open={carPartsHelpOpen}
                      onOpenChange={setCarPartsHelpOpen}
                      url={sectionTutorials.car_parts}
                      title="How to use car parts addon?"
                    />
                  </>
                ) : null}
                <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="pf-car-parts-universal-row">
                  <Switch
                    checked={Boolean(form.car_parts?.universal)}
                    onCheckedChange={(v) => setForm({ ...form, car_parts: { ...form.car_parts, universal: v } })}
                    data-testid="pf-car-parts-universal"
                  />
                  Universal fit — this part works on any vehicle
                </label>
                {!form.car_parts?.universal ? (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500" data-testid="pf-car-parts-reuse-hint">Previously saved Make, Model, Year, and Trim appear as suggestions. Pick one to reuse or type a new value.</p>
                    {(form.car_parts.applications || []).map((row, ri) => {
                      const apps = form.car_parts.applications || [];
                      const tree = fitmentGarage.tree || {};
                      const makes = uniqueMakes(tree, apps);
                      const models = uniqueModels(tree, row.make, apps);
                      const years = uniqueYears(tree, row.make, row.model, apps);
                      const trims = uniqueTrims(tree, row.make, row.model, row.year_from || row.year_to, apps);
                      const patchRow = (patch) => {
                        const applications = [...apps];
                        applications[ri] = { ...row, ...patch };
                        setForm({ ...form, car_parts: { ...form.car_parts, applications } });
                      };
                      return (
                      <div key={row.id || ri} className="rounded-xl border border-slate-200 p-3 grid sm:grid-cols-2 lg:grid-cols-6 gap-2" data-testid={`pf-car-parts-row-${ri}`}>
                        <div>
                          <Label className="text-[11px]">Make</Label>
                          <Input className="mt-1" list={`pf-car-parts-make-opts-${ri}`} data-testid={`pf-car-parts-make-${ri}`} placeholder="Honda" value={row.make || ""} onChange={(e) => patchRow({ make: e.target.value })} />
                          <datalist id={`pf-car-parts-make-opts-${ri}`} data-testid={`pf-car-parts-make-list-${ri}`}>{makes.map((m) => <option key={m} value={m} />)}</datalist>
                        </div>
                        <div>
                          <Label className="text-[11px]">Model</Label>
                          <Input className="mt-1" list={`pf-car-parts-model-opts-${ri}`} data-testid={`pf-car-parts-model-${ri}`} placeholder="City" value={row.model || ""} onChange={(e) => patchRow({ model: e.target.value })} />
                          <datalist id={`pf-car-parts-model-opts-${ri}`} data-testid={`pf-car-parts-model-list-${ri}`}>{models.map((m) => <option key={m} value={m} />)}</datalist>
                        </div>
                        <div>
                          <Label className="text-[11px]">Year from</Label>
                          <Input className="mt-1" type="number" list={`pf-car-parts-year-from-opts-${ri}`} data-testid={`pf-car-parts-year-from-${ri}`} placeholder="2014" value={row.year_from ?? ""} onChange={(e) => patchRow({ year_from: e.target.value })} />
                          <datalist id={`pf-car-parts-year-from-opts-${ri}`}>{years.map((y) => <option key={y} value={y} />)}</datalist>
                        </div>
                        <div>
                          <Label className="text-[11px]">Year to</Label>
                          <Input className="mt-1" type="number" list={`pf-car-parts-year-to-opts-${ri}`} data-testid={`pf-car-parts-year-to-${ri}`} placeholder="2019" value={row.year_to ?? ""} onChange={(e) => patchRow({ year_to: e.target.value })} />
                          <datalist id={`pf-car-parts-year-to-opts-${ri}`}>{years.map((y) => <option key={`to-${y}`} value={y} />)}</datalist>
                        </div>
                        <div>
                          <Label className="text-[11px]">Trim <span className="font-normal text-slate-400">(optional)</span></Label>
                          <Input className="mt-1" list={`pf-car-parts-trim-opts-${ri}`} data-testid={`pf-car-parts-trim-${ri}`} placeholder="VX / ZX" value={row.trim || ""} onChange={(e) => patchRow({ trim: e.target.value })} />
                          <datalist id={`pf-car-parts-trim-opts-${ri}`} data-testid={`pf-car-parts-trim-list-${ri}`}>{trims.map((t) => <option key={t} value={t} />)}</datalist>
                        </div>
                        <div className="flex items-end">
                          <button type="button" className="h-10 px-3 text-xs text-red-600" data-testid={`pf-car-parts-remove-${ri}`} onClick={() => {
                            const applications = (form.car_parts.applications || []).filter((_, i) => i !== ri);
                            setForm({ ...form, car_parts: { ...form.car_parts, applications } });
                          }}><Trash2 className="w-4 h-4 inline mr-1" /> Remove</button>
                        </div>
                      </div>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm" data-testid="pf-car-parts-add" onClick={() => setForm({
                      ...form,
                      car_parts: { ...form.car_parts, applications: [...(form.car_parts.applications || []), newFitmentRow()] },
                    })}>
                      <Plus className="w-4 h-4 mr-1" /> Add vehicle
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Card>
        )}

        {formAddon?.entitlement?.enabled && (
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-custom-form">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Custom Form</Label>
                <p className="text-xs text-slate-500 mt-1">Optional override for this product only. Prefer building forms in <Link to="/admin/addons/custom-form" className="underline">App Store → Custom Form</Link> and linking products or categories. Enabling a form here hides the library form on this product.</p>
              </div>
              <Switch checked={Boolean(form.custom_form?.enabled)} onCheckedChange={(v) => setForm({ ...form, custom_form: { ...(form.custom_form || {}), enabled: v, fields: form.custom_form?.fields || [], cta: form.custom_form?.cta || "Get Started" } })} data-testid="pf-cf-enabled" />
            </div>
            {form.custom_form?.enabled && (
              <div className="space-y-3">
                <div>
                  <Label>Button text</Label>
                  <Input className="mt-1" data-testid="pf-cf-cta" value={form.custom_form.cta || ""} onChange={(e) => setForm({ ...form, custom_form: { ...form.custom_form, cta: e.target.value } })} />
                </div>
                {(form.custom_form.fields || []).map((field, fi) => (
                  <div key={field.id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`pf-cf-field-${fi}`}>
                    <div className="flex gap-2">
                      <Input value={field.label} onChange={(e) => {
                        const fields = [...form.custom_form.fields];
                        fields[fi] = { ...field, label: e.target.value };
                        setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                      }} />
                      <span className="text-xs text-slate-500 self-center shrink-0">{field.type.replace("_", " ")}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, custom_form: { ...form.custom_form, fields: form.custom_form.fields.filter((_, i) => i !== fi) } })}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    {field.type !== "paid_dropdown" && field.type !== "quantity" && (
                      <div>
                        <Label className="text-xs">Surcharge</Label>
                        <Input type="number" step="0.01" className="mt-1 font-mono-nums" value={field.price} onChange={(e) => {
                          const fields = [...form.custom_form.fields];
                          fields[fi] = { ...field, price: e.target.value };
                          setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                        }} />
                      </div>
                    )}
                    {(field.type === "paid_dropdown" || field.type === "quantity") && (
                      <div className="space-y-2">
                        {(field.options || []).map((opt, oi) => (
                          <div key={opt.id} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <Input placeholder="Label" value={opt.label} onChange={(e) => {
                              const fields = [...form.custom_form.fields];
                              const options = [...field.options];
                              options[oi] = { ...opt, label: e.target.value };
                              fields[fi] = { ...field, options };
                              setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                            }} />
                            {field.type === "quantity" && (
                              <Input type="number" placeholder="Qty" value={opt.qty} onChange={(e) => {
                                const fields = [...form.custom_form.fields];
                                const options = [...field.options];
                                options[oi] = { ...opt, qty: e.target.value };
                                fields[fi] = { ...field, options };
                                setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                              }} />
                            )}
                            <Input type="number" step="0.01" placeholder="Price" value={opt.price} onChange={(e) => {
                              const fields = [...form.custom_form.fields];
                              const options = [...field.options];
                              options[oi] = { ...opt, price: e.target.value };
                              fields[fi] = { ...field, options };
                              setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                            }} />
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="outline" onClick={() => {
                          const fields = [...form.custom_form.fields];
                          fields[fi] = { ...field, options: [...(field.options || []), { id: `op_${uid().slice(0, 8)}`, label: "New", price: 0, qty: field.type === "quantity" ? 1 : undefined }] };
                          setForm({ ...form, custom_form: { ...form.custom_form, fields } });
                        }}><Plus className="w-3 h-3 mr-1" /> Option</Button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  {["paid_name", "paid_dropdown", "paid_image", "quantity"].map((t) => (
                    <Button key={t} type="button" size="sm" variant="outline" data-testid={`pf-cf-add-${t}`} onClick={() => setForm({ ...form, custom_form: { ...form.custom_form, enabled: true, fields: [...(form.custom_form.fields || []), newCfField(t)] } })}>
                      <Plus className="w-3 h-3 mr-1" /> {t.replace("paid_", "").replace("_", " ")}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {currencyAddon?.entitlement?.enabled && enabledCountries.length > 0 && (
          <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-country-prices">
            <div>
              <Label>Prices by country</Label>
              <p className="text-xs text-slate-500 mt-1">Visitors from each country only see that country&apos;s price. Default INR price above is the fallback.</p>
            </div>
            <div className="space-y-2">
              {enabledCountries.map((c) => {
                const row = form.country_prices?.[c.code] || {};
                return (
                  <div key={c.code} className="grid grid-cols-3 gap-2 items-end" data-testid={`pf-country-${c.code}`}>
                    <div className="text-sm font-medium pt-6">{c.name} ({c.symbol})</div>
                    <div>
                      <Label className="text-xs">Price</Label>
                      <Input type="number" step="0.01" className="mt-1 font-mono-nums" value={row.price ?? ""} onChange={(e) => setForm({ ...form, country_prices: { ...form.country_prices, [c.code]: { ...row, price: e.target.value } } })} />
                    </div>
                    <div>
                      <Label className="text-xs">MRP</Label>
                      <Input type="number" step="0.01" className="mt-1 font-mono-nums" value={row.mrp ?? ""} onChange={(e) => setForm({ ...form, country_prices: { ...form.country_prices, [c.code]: { ...row, mrp: e.target.value } } })} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="p-6 space-y-4 border-slate-200/70">
          <div className="flex items-center justify-between">
            <div>
              <Label>Product gallery</Label>
              <p className="text-xs text-slate-500 mt-1">Up to 8 slides. First slide is the cover — use arrows to put a YouTube video first, or 2nd, 3rd, any position.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload images"}
              <input type="file" accept="image/*" multiple hidden data-testid="pf-image-input" onChange={(e) => e.target.files?.length && upload(e.target.files)} />
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="pf-youtube-url" placeholder="https://www.youtube.com/watch?v=…" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} className="flex-1" />
            <Button type="button" variant="outline" data-testid="pf-youtube-add" onClick={addYoutube}><Youtube className="w-4 h-4 mr-1" /> Attach YouTube</Button>
          </div>
          {form.images.length === 0 ? (
            <div className="p-8 rounded-lg border-2 border-dashed border-slate-300 text-center text-sm text-slate-500">No slides yet. Upload photos or attach a YouTube video.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.images.map((item, i) => {
                const slide = parseGalleryItem(item);
                const src = slide?.type === "youtube" ? youtubeThumb(slide.video_id) : mediaUrl(slide?.url);
                return (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group border border-slate-200" data-testid={`pf-slide-${i}`}>
                  {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null}
                  {slide?.type === "youtube" && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="w-10 h-10 rounded-full bg-red-600/90 text-white flex items-center justify-center"><Youtube className="w-5 h-5" /></span>
                    </div>
                  )}
                  {i === 0 && <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-900 text-white">COVER</div>}
                  {slide?.type === "youtube" && <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-600 text-white">VIDEO</div>}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} data-testid={`pf-image-left-${i}`} className="w-7 h-7 rounded-full bg-white text-slate-900 flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                    <button type="button" onClick={() => moveImg(i, 1)} disabled={i === form.images.length - 1} data-testid={`pf-image-right-${i}`} className="w-7 h-7 rounded-full bg-white text-slate-900 flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
                    <button type="button" onClick={() => removeImg(i)} data-testid={`pf-image-remove-${i}`} className="w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pf-3d-card">
          <div>
            <Label>3D model (optional)</Label>
            <p className="text-xs text-slate-500 mt-1">Upload a .glb or .gltf file. Shoppers can rotate it on the product page. Prefer GLB (one file). GLTF works when buffers are embedded in the file.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
              <Box className="w-4 h-4" /> Upload GLB / GLTF
              <input
                type="file"
                accept=".glb,.gltf,model/gltf-binary,model/gltf+json"
                hidden
                data-testid="pf-glb-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const data = await uploadFile("/merchant/media/upload-model", fd);
                    setForm((f) => ({ ...f, model_3d_url: data.url }));
                    toast.success("3D model uploaded");
                  } catch (err) { toast.error(extractErr(err)); }
                }}
              />
            </label>
            {form.model_3d_url && (
              <button type="button" className="text-xs text-red-600" data-testid="pf-glb-remove" onClick={() => setForm((f) => ({ ...f, model_3d_url: "" }))}>Remove 3D model</button>
            )}
          </div>
          {form.model_3d_url && (
            <div className="aspect-square max-w-sm rounded-xl overflow-hidden border border-slate-200 bg-slate-50" data-testid="pf-glb-preview">
              <ProductModelViewer src={form.model_3d_url} alt={form.name || "3D model"} />
            </div>
          )}
        </Card>

        <Card className="p-6 space-y-5 border-slate-200/70">
          <div>
            <Label className="text-base">Variants <span className="text-slate-400 text-xs font-normal">(size, color, or any custom option)</span></Label>
            <p className="text-xs text-slate-500 mt-1">Define options like Size or Color, then generate every combination in one click — Shopify style. Leave empty if this product has no options.</p>
          </div>

          {/* ---- Options builder ---- */}
          <div className="space-y-3">
            {options.map((o, oi) => (
              <div key={o.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/60 space-y-3" data-testid={`pf-option-${oi}`}>
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-xs">Option name</Label>
                    <Input placeholder="e.g. Size, Color, Material" data-testid={`pf-option-name-${oi}`} value={o.name} onChange={(e) => updateOption(o.id, { name: e.target.value })} className="mt-1" />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Type</Label>
                    <Select value={o.type} onValueChange={(v) => updateOption(o.id, { type: v })}>
                      <SelectTrigger data-testid={`pf-option-type-${oi}`} className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text (e.g. S, M, L)</SelectItem>
                        <SelectItem value="color">Color (with picker)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeOption(o.id)} data-testid={`pf-option-remove-${oi}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                </div>

                <div>
                  <Label className="text-xs">Values</Label>
                  <div className="mt-1 space-y-2">
                    {o.values.length === 0 && <p className="text-xs text-slate-400">No values yet — add one below.</p>}
                    {o.values.map((val, vi) => (
                      <div key={val.id} className="flex items-center gap-2">
                        {o.type === "color" && (
                          <>
                            <input type="color" value={val.hex} onChange={(e) => updateOptionValue(o, val.id, { hex: e.target.value })} data-testid={`pf-option-${oi}-color-${vi}`} className="w-9 h-9 rounded-lg border border-slate-200 bg-white cursor-pointer p-0.5 flex-shrink-0" title="Pick colour" />
                            <span className="w-9 h-9 rounded-lg border border-slate-200 flex-shrink-0" style={{ background: val.hex }} />
                          </>
                        )}
                        <Input placeholder={o.type === "color" ? "Colour name (e.g. Sky Blue)" : "Value (e.g. Medium)"} data-testid={`pf-option-${oi}-value-${vi}`} value={val.value} onChange={(e) => updateOptionValue(o, val.id, { value: e.target.value })} />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeOptionValue(o, val.id)} data-testid={`pf-option-${oi}-value-remove-${vi}`} className="text-slate-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => addOptionValue(o)} data-testid={`pf-option-addvalue-${oi}`} className="rounded-full">
                      {o.type === "color" ? <Palette className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Add value
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              {options.length < 3 && (
                <Button type="button" variant="outline" onClick={addOption} data-testid="pf-add-option" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Add option</Button>
              )}
              {options.length > 0 && (
                <Button type="button" onClick={generateVariants} data-testid="pf-generate-variants" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"><Wand2 className="w-4 h-4 mr-1" /> Generate variants</Button>
              )}
            </div>
          </div>

          {/* ---- Generated / manual variants list ---- */}
          <div className="pt-1 border-t border-slate-100">
            <div className="flex items-center justify-between pt-4">
              <Label className="text-sm">{(form.variants || []).length} variant{(form.variants || []).length === 1 ? "" : "s"}</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addManualVariant} data-testid="pf-add-variant" className="text-blue-600 hover:text-blue-700"><Plus className="w-4 h-4 mr-1" /> Add variant manually</Button>
            </div>

            {(form.variants || []).length > 0 && (
              <div className="mt-3 space-y-3">
                {form.variants.map((v, idx) => {
                  const attrPairs = Object.entries(v.attributes || {}).filter(([k]) => !k.endsWith("_hex"));
                  return (
                    <div key={v.id} className="p-4 rounded-lg border border-slate-200 bg-white space-y-3" data-testid={`pf-variant-${idx}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          {attrPairs.length > 0 ? attrPairs.map(([k, val]) => {
                            const hex = v.attributes?.[`${k}_hex`];
                            return (
                              <span key={k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-700">
                                {hex && <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: hex }} />}
                                <span className="text-slate-400 capitalize">{k}:</span> {val}
                              </span>
                            );
                          }) : (
                            <Input placeholder="Variant name (e.g. Gift wrap)" data-testid={`pf-variant-name-${idx}`} value={v.name} onChange={(e) => updateVariant(idx, { name: e.target.value })} className="h-8 w-56" />
                          )}
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(idx)} data-testid={`pf-variant-remove-${idx}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Price (₹) <span className="text-slate-400">override</span></Label>
                          <Input type="number" data-testid={`pf-variant-price-${idx}`} value={v.price ?? ""} placeholder="uses main" onChange={(e) => updateVariant(idx, { price: e.target.value })} className="mt-1 font-mono-nums" />
                        </div>
                        <div>
                          <Label className="text-xs">MRP (₹) <span className="text-slate-400">override</span></Label>
                          <Input type="number" data-testid={`pf-variant-mrp-${idx}`} value={v.mrp ?? ""} placeholder="uses main" onChange={(e) => updateVariant(idx, { mrp: e.target.value })} className="mt-1 font-mono-nums" />
                        </div>
                        <div>
                          <Label className="text-xs">Stock <span className="text-slate-400">blank = unlimited</span></Label>
                          <Input type="number" min="0" data-testid={`pf-variant-stock-${idx}`} placeholder="Unlimited" value={v.stock ?? ""} onChange={(e) => updateVariant(idx, { stock: e.target.value })} className="mt-1 font-mono-nums" />
                        </div>
                        <div>
                          <Label className="text-xs">SKU</Label>
                          <Input data-testid={`pf-variant-sku-${idx}`} value={v.sku || ""} onChange={(e) => updateVariant(idx, { sku: e.target.value })} className="mt-1 font-mono-nums" />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Variant image <span className="text-slate-400 font-normal">(shown on product page when selected)</span></Label>
                        <div className="mt-1">
                          <VariantImage url={v.image_url} testid={`pf-variant-image-${idx}`} onChange={(url) => updateVariant(idx, { image_url: url })} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6 space-y-4 border-slate-200/70">
          <div className="flex items-center justify-between">
            <div><Label>Active</Label><p className="text-xs text-slate-500">Visible in the storefront</p></div>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="pf-active" />
          </div>
          <div className="flex items-center justify-between">
            <div><Label>Featured</Label><p className="text-xs text-slate-500">Shown on homepage</p></div>
            <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="pf-featured" />
          </div>
        </Card>

        <Card className="p-6 space-y-4 border-slate-200/70">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Fake rating</Label>
              <p className="text-xs text-slate-500 mt-1">Show a seed rating on the storefront to build trust for new products. Real customer ratings blend in on top of this.</p>
            </div>
            <Switch checked={form.fake_rating_enabled} onCheckedChange={(v) => setForm({ ...form, fake_rating_enabled: v })} data-testid="pf-fake-rating" />
          </div>
          {form.fake_rating_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
              <div>
                <Label>Average (0–5)</Label>
                <Input type="number" step="0.1" min="0" max="5" data-testid="pf-fake-avg" value={form.fake_rating_avg} onChange={(e) => setForm({ ...form, fake_rating_avg: e.target.value })} className="mt-1 font-mono-nums" placeholder="e.g. 4.6" />
              </div>
              <div>
                <Label>Number of ratings</Label>
                <Input type="number" min="0" data-testid="pf-fake-count" value={form.fake_rating_count} onChange={(e) => setForm({ ...form, fake_rating_count: e.target.value })} className="mt-1 font-mono-nums" placeholder="e.g. 128" />
              </div>
              <div className="col-span-2 text-xs text-slate-500">Preview: <span className="font-semibold text-amber-600">{form.fake_rating_avg || "0"} ★</span> · {form.fake_rating_count || 0} ratings</div>
            </div>
          )}
        </Card>
        </>)}

        <div className="flex gap-3">
          <Button type="submit" disabled={saving || (!isEdit && quota && quota.remaining <= 0)} data-testid="pf-submit" className="rounded-full bg-blue-600 hover:bg-blue-700">{saving ? t("common.save") : t("products.save")}</Button>
          <Button type="button" variant="outline" onClick={() => nav("/admin/products")} data-testid="pf-cancel">Cancel</Button>
        </div>
      </form>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="pf-newcat-name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="mt-1" placeholder="e.g. Snacks" />
            </div>
            <div>
              <Label>Category image <span className="text-slate-400 text-xs font-normal">optional</span></Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {newCat.image_url ? <img src={mediaUrl(newCat.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                </div>
                <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer" data-testid="pf-newcat-image">
                  <Upload className="w-4 h-4" /> {catUploading ? "Uploading…" : (newCat.image_url ? "Replace" : "Upload")}
                  <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && uploadCatImage(e.target.files[0])} />
                </label>
                {newCat.image_url && <button type="button" className="text-xs text-red-600" onClick={() => setNewCat({ ...newCat, image_url: "" })}>Remove</button>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button type="button" onClick={createCategory} disabled={catSaving} data-testid="pf-newcat-save" className="bg-blue-600 hover:bg-blue-700">{catSaving ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New tag</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="pf-newtag-name" value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} className="mt-1" placeholder="e.g. Summer" />
            </div>
            <div>
              <Label>Tag image <span className="text-slate-400 text-xs font-normal">optional</span></Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {newTag.image_url ? <img src={mediaUrl(newTag.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                </div>
                <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 cursor-pointer" data-testid="pf-newtag-image">
                  <Upload className="w-4 h-4" /> {tagUploading ? "Uploading…" : (newTag.image_url ? "Replace" : "Upload")}
                  <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && uploadTagImage(e.target.files[0])} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTagOpen(false)}>Cancel</Button>
            <Button type="button" onClick={createTag} disabled={tagSaving} data-testid="pf-newtag-save" className="bg-blue-600 hover:bg-blue-700">{tagSaving ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
